import React, { useContext, useEffect, useState } from "react";
import { Text, CustomTable } from "react_reusable_components";
import { useIntl } from "react-intl";
import { EditOutlined, EyeOutlined } from "@ant-design/icons";
import debounce from "lodash.debounce";

import useResponsive from "../../core/hooks/useResponsive";
import TableActions from "../../components/TableActions";
import QuestionnaireListingTopContainer from "../QuestionnaireListingTopContainer/QuestionnaireListingTopContainer";
import AssignSubProductCategoryToQuestionnaireModal from "../AssignSubProductCategoryToQuestionnaireModal";
import ConfirmModalContainer from "../ConfirmModalContainer/ConfirmModalContainer";
import ViewCountryWiseQuestionnaireModal from "../ViewCountryWiseQuestionnaireModal/ViewCountryWiseQuestionnaireModal";
import QuestionnaireService from "../../services/questionnaire-service";
import { columns } from "./QuestionnaireListingColumns";
import { QuestionnaireContext } from "../../globalContext/questionnaire/questionnaireProvider";
import { setQuestionnaireQuery } from "../../globalContext/questionnaire/questionnaireActions";
import { TwoRow } from "../../core/layouts";
import { GlobalLoaderContext } from "../../globalContext/globalLoader/globalLoaderProvider";
import { getLocalizedMessage } from "../../UtilityFunctions/utils";
import { PAGE_LIMIT, recordsPerPageOptions, userRole } from "../../constants";
import variables from "../../themes/base/styles/variables";
import Styles from "./QuestionnaireListingContainer.module.scss";
import { useNavigate } from "react-router-dom";
import { showNotification } from "../../UtilityFunctions/notification";
import { AuthContext } from "../../globalContext/auth/authProvider";

export default function QuestionnaireListingContainer() {
  const responsive = useResponsive();
  const intl = useIntl();
  const navigate = useNavigate();
  const [authState] = useContext(AuthContext);

  const [questionnaireState, questionnaireDispatch] =
    useContext(QuestionnaireContext);
  const loaderDispatch = useContext(GlobalLoaderContext)[1];
  const [query, setQuery] = useState({
    page: questionnaireState?.query?.page || 1,
    recordsPerPage: questionnaireState?.query?.recordsPerPage || PAGE_LIMIT,
    searchStr: questionnaireState?.query?.searchStr || "",
  });
  const [selectedRow, setSelectedRow] = useState(null);
  const [activeRecord, setActiveRecord] = useState({});
  const [openAssignSubProductModal, setOpenAssignSubProductModal] =
    useState(false);

  const [openBaseFormModal, setOpenBaseFormModal] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [
    viewCountryWiseQuestionnaireModal,
    setViewCountryWiseQuestionnaireModal,
  ] = useState(false);
  const [countriesListData, setCountriesListData] = useState([]);

  const [filter, setFilter] = useState({
    questionnaireType: questionnaireState?.query || [],
    productCategories: questionnaireState?.query?.productCategories || [],
    subCategories: questionnaireState?.query?.subCategories || [],
  });

  useEffect(() => {
    let queryParams = getQuestionnaireListQueryParams(query, filter);
    questionnaireDispatch(
      setQuestionnaireQuery({
        ...questionnaireState?.query,
        ...query,
        ...filter,
      })
    );

    const debouncedApiCall = debounce(() => {
      QuestionnaireService.getQuestionnaireList({
        dispatch: questionnaireDispatch,
        loaderDispatch: loaderDispatch,
        params: queryParams,
        intl,
      });
    }, 400);

    debouncedApiCall();
  }, [query, filter]);

  useEffect(() => {
    setSelectedRow(null);
  }, [questionnaireState?.questionnaireList]);

  const tableActionsOptions = (record) =>
    [
      {
        label: getLocalizedMessage(intl, "actionsOption.viewDetails"),
        icon: <EyeOutlined className={Styles["icon-style"]} />,
        action: () => {
          navigate(`/view-questionnaire/${record.questionnaireCode}`);
        },
      },
      {
        label: getLocalizedMessage(intl, "actionsOption.editDetails"),
        icon: <EditOutlined className={Styles["icon-style"]} />,
        action: () =>
          navigate(`/edit-questionnaire/${record.questionnaireCode}`),
      },
      authState?.currentRole?.id === userRole.ADMIN && {
        label: getLocalizedMessage(
          intl,
          "actionsOption.viewCountryWiseQuestionnaire"
        ),
        icon: <EyeOutlined className={Styles["icon-style"]} />,
        action: () => {
          setActiveRecord(record);
          checkSubCategoryExistToQuestionnaireHandler(record);
        },
      },
      record?.isRepair &&
        authState?.currentRole?.id === userRole.ADMIN && {
          label: getLocalizedMessage(
            intl,
            "actionsOption.assignSubProductCategory"
          ),
          icon: (
            <img
              alt="block"
              src={"/icons/assignment.svg"}
              className={Styles["icon-style"]}
            />
          ),
          action: () => {
            setActiveRecord(record);
            setOpenAssignSubProductModal(true);
          },
        },
    ].filter((item) => item);

  const submitHandler = (productSubCategoryId) => {
    let queryParams = {
      pageLimit: query.recordsPerPage,
      pageSkip: query.recordsPerPage * (query.page - 1),
    };
    QuestionnaireService.assignSubProductCategoryToQuestionnaire({
      loaderDispatch,
      questionnaireCode: activeRecord?.questionnaireCode,
      body: { productSubCategoryId },
      intl,
    }).then(() => {
      QuestionnaireService.getQuestionnaireList({
        dispatch: questionnaireDispatch,
        loaderDispatch: loaderDispatch,
        params: queryParams,
      });
    });
  };

  const downloadQuestionnaireReportsHandler = () => {
    let queryParams = getQuestionnaireListQueryParams(query, filter);

    delete queryParams.pageLimit;
    delete queryParams.pageSkip;

    if (questionnaireState?.questionnaireList?.result?.length)
      QuestionnaireService.getDownloadQuestionnaireReports({
        params: queryParams,
        intl,
        loaderDispatch: loaderDispatch,
      });
    else
      showNotification(
        "error",
        "Error",
        getLocalizedMessage(intl, "reports.cannotGenerateData")
      );
  };

  const checkSubCategoryExistToQuestionnaireHandler = (questionnaireRecord) => {
    const productSubCategoryId = questionnaireRecord?.productSubCategory?.id;
    if (questionnaireRecord?.isOperational) {
      QuestionnaireService.getCountriesForQuestionnaire({
        loaderDispatch,
        productSubCategoryId,
        intl,
      }).then((data) => {
        setCountriesListData(data?.data?.data);
        setViewCountryWiseQuestionnaireModal(true);
      });
    } else {
      if (!productSubCategoryId) {
        setOpenModal(true);
      } else {
        QuestionnaireService.getCountriesForQuestionnaire({
          loaderDispatch,
          productSubCategoryId,
          intl,
        }).then((res) => {
          if (!res?.data?.data) {
            setOpenBaseFormModal(true);
          } else {
            setCountriesListData(res?.data?.data);
            setViewCountryWiseQuestionnaireModal(true);
          }
        });
      }
    }
  };

  return (
    <>
      <TwoRow
        className={Styles["main-container"]}
        topSection={
          <QuestionnaireListingTopContainer
            query={query}
            setQuery={setQuery}
            filter={filter}
            setFilter={setFilter}
            downloadQuestionnaireReportsHandler={
              downloadQuestionnaireReportsHandler
            }
          />
        }
        bottomSection={
          <CustomTable
            locale={{
              emptyText: !questionnaireState?.questionnaireList?.result && (
                <div style={{ minHeight: variables.customTableHeight }} />
              ),
            }}
            onRow={(record) => {
              return {
                onClick: () => {
                  if (record?.questionnaireCode === selectedRow)
                    setSelectedRow(null);
                  else setSelectedRow(record.questionnaireCode);
                },
              };
            }}
            sticky={{ offsetHeader: 0 }}
            isMobile={!responsive.isSm}
            columns={[
              ...columns(intl, responsive.isMd, authState),
              {
                title: <></>,
                dataIndex: "operation",
                key: "operation",
                align: "center",
                width: "5%",
                render: (_, record) => {
                  return (
                    <TableActions
                      openPopover={selectedRow === record.questionnaireCode}
                      tableActionsOptions={tableActionsOptions(record)}
                    />
                  );
                },
              },
            ]}
            dataSource={questionnaireState?.questionnaireList?.result?.map(
              (data, idx) => ({
                ...data,
                key: idx,
              })
            )}
            query={query}
            setQuery={setQuery}
            totalCount={parseInt(
              questionnaireState?.questionnaireList?.totalCount
            )}
            prevText={getLocalizedMessage(intl, "pagination.prevText")}
            nextText={getLocalizedMessage(intl, "pagination.nextText")}
            sizeChangeOptions={recordsPerPageOptions.map((rec) => ({
              value: rec,
              label: (
                <Text className={Styles["select-item-style"]}>
                  {rec}&nbsp;
                  {getLocalizedMessage(intl, "pagination.selectLabelSuffix")}
                </Text>
              ),
            }))}
            tableStyleClassName={`${Styles["custom-table-style"]} ${
              Styles[!responsive?.isSm && "custom-table-mobile-style"]
            } `}
          />
        }
      />

      {openAssignSubProductModal && (
        <AssignSubProductCategoryToQuestionnaireModal
          open={openAssignSubProductModal}
          onClose={() => setOpenAssignSubProductModal(false)}
          questionnaireName={activeRecord?.name}
          productCategory={activeRecord?.productCategory}
          productSubCategory={activeRecord?.productSubCategory}
          submitHandler={submitHandler}
          questionnaireId={activeRecord?.id}
        />
      )}

      <ConfirmModalContainer
        open={openBaseFormModal}
        setOpen={setOpenBaseFormModal}
        question={getLocalizedMessage(
          intl,
          "modal.noCountryAssignedToQuestionnaire.title"
        )}
        iconSrc={"/icons/warningIcon.svg"}
        descriptionText={getLocalizedMessage(
          intl,
          "modal.viewBaseForm.description",
          {
            questionnaireName: activeRecord?.name,
          }
        )}
        restStyle={{ backgroundColor: variables.primaryBg }}
        cancelText={getLocalizedMessage(intl, "button.text.goBack")}
        confirmText={getLocalizedMessage(intl, "button.text.questionnaireView")}
        onCancel={() => setOpenBaseFormModal(false)}
        onConfirm={() => {
          navigate(`/view-questionnaire/${activeRecord?.questionnaireCode}`);
        }}
      />
      <ConfirmModalContainer
        open={openModal}
        setOpen={setOpenModal}
        question={getLocalizedMessage(intl, "modal.noCategoryAssigned.title")}
        iconSrc={"/icons/warningIcon.svg"}
        descriptionText={getLocalizedMessage(
          intl,
          "modal.noSubCategoryAssigned.description",
          { questionnaireName: activeRecord?.name }
        )}
        restStyle={{ backgroundColor: variables.primaryBg }}
        onCancel={() => setOpenModal(false)}
        onConfirm={() => setOpenModal(false)}
        hideButton
      />
      {viewCountryWiseQuestionnaireModal && (
        <ViewCountryWiseQuestionnaireModal
          open={viewCountryWiseQuestionnaireModal}
          countryOptions={countriesListData}
          activeRecord={activeRecord}
          onClose={() => setViewCountryWiseQuestionnaireModal(false)}
        />
      )}
    </>
  );
}

function getQuestionnaireListQueryParams(query, filter) {
  let queryParams = {
    pageLimit: query.recordsPerPage,
    pageSkip: query.recordsPerPage * (query.page - 1),
  };
  if (query.searchStr.length > 0) {
    queryParams = {
      ...queryParams,
      searchStr: encodeURI(query.searchStr),
    };
  }
  if (filter?.productCategories?.length > 0) {
    queryParams = {
      ...queryParams,
      productCategoryId: filter.productCategories.join(","),
    };
  }
  if (filter?.subCategories?.length > 0) {
    queryParams = {
      ...queryParams,
      subProductCategoryId: filter.subCategories.join(","),
    };
  }
  if (filter?.questionnaireType?.length > 0) {
    queryParams = {
      ...queryParams,
      type: filter?.questionnaireType.join(","),
    };
  }
  return queryParams;
}
